import { createContext, JSX, useState, useContext } from "react";
import { ProjectService } from "@app/projects";
import ProviderTerminationService from "@app/provider-terminations/providerTerminationService";
import { PciDashboardService } from "@app/projects/network-assessment/pci-dashboard/shared";
import { reportName } from "@app/exports/export-panel-body/exportPanelBodyHelpers";
import { IncludedReports, TerminationsWizardContextSteps, BulkProviderTerminationMethod } from "@app/terminations/import/interfaces";
import { BulkProviderChangeProjectTableData, InvalidNpiTableData, InvalidTinTableData, ResultsSummaryTableData, ReviewProjectsTableData } from "@app/shared/provider-change-wizards/interfaces";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { Features } from "@app/feature-flags";

export type VolumeTerminationType = "import" | "ghost";

interface VolumeTerminationsProps {
  terminationType: VolumeTerminationType;
}

interface TerminationsWizardContextProps {
  projectService: ProjectService;
  providerTerminationService: ProviderTerminationService;
  pciDashboardService: PciDashboardService;

  bulkProviderTerminationId: number;
  bulkProviderTerminationMethod: BulkProviderTerminationMethod;
  bulkImportFormData: FormData;
  comment: string;
  emailOnSuccess: boolean;
  filename: string;
  formatType: string;
  ghostCountsCalculated: boolean;
  includedReports: IncludedReports;
  invalidNpis: InvalidNpiTableData[];
  invalidTins: InvalidTinTableData[];
  isDistinctServicingProviderReport: boolean;
  projects: BulkProviderChangeProjectTableData[];
  resultsSummary: ResultsSummaryTableData[];
  reviewProjects: ReviewProjectsTableData[];
  page: TerminationsWizardContextSteps;
  selectedLineOfBusiness: string;
  selectedProjectIds: string[];
  terminateByAllLocations: boolean;
  terminateByAllSpecialties: boolean;
  terminateByNpi: boolean;
  terminateByTin: boolean;
  terminationReason: string;
  terminationType: VolumeTerminationType;

  setBulkProviderTerminationId: (id: number) => void;
  setBulkProviderTerminationMethod: (termintionMethod: BulkProviderTerminationMethod) => void;
  setBulkImportFormData: (formData: FormData) => void;
  setComment: (newComment: string) => void;
  setEmailOnSuccess: (flag: boolean) => void;
  setFormatType: (newType: string) => void;
  setFilename: (newFilename: string) => void;
  setGhostCountsCalculated: (isCalculated: boolean) => void;
  setInvalidNpis: (invalidNpis: InvalidNpiTableData[]) => void;
  setInvalidTins: (invalidTins: InvalidTinTableData[]) => void;
  setIsDistinctServicingProviderReport: (isDistinct: boolean) => void;
  setPage: (newPage: TerminationsWizardContextSteps) => void;
  setProjects: (projects: BulkProviderChangeProjectTableData[]) => void;
  setResultsSummary: (results: ResultsSummaryTableData[]) => void;
  setReviewProjects: (projects: ReviewProjectsTableData[]) => void;
  setSelectedLineOfBusiness: (lineOfBusiness: string) => void;
  setSelectedProjectIds: (ids: string[]) => void;
  setTerminateByAllLocations: (flag: boolean) => void;
  setTerminateByAllSpecialties: (flag: boolean) => void;
  setTerminateByNpi: (flag: boolean) => void;
  setTerminateByTin: (flag: boolean) => void;
  setTerminationReason: (newReason: string) => void;
  updateIncludedReports: (reportType: keyof IncludedReports, newValue: boolean) => void;
}

const TerminationsWizardContext = createContext<Partial<TerminationsWizardContextProps>>({
  /* eslint-disable sort-keys -- these are separated by global services, getters, and setters */
  projectService: null,
  providerTerminationService: null,
  pciDashboardService: null,

  bulkImportFormData: null,
  bulkProviderTerminationId: null,
  bulkProviderTerminationMethod: null,
  comment: "",
  emailOnSuccess: true,
  filename: "",
  formatType: "CSV",
  ghostCountsCalculated: false,
  includedReports: null,
  invalidNpis: [],
  invalidTins: [],
  isDistinctServicingProviderReport: false,
  page: TerminationsWizardContextSteps.SelectProjects,
  projects: [],
  resultsSummary: [],
  reviewProjects: [],
  selectedLineOfBusiness: "Total",
  selectedProjectIds: [],
  terminateByAllLocations: true,
  terminateByAllSpecialties: true,
  terminateByNpi: true,
  terminateByTin: false,
  terminationReason: "",

  setBulkImportFormData: () => null,
  setBulkProviderTerminationId: () => null,
  setBulkProviderTerminationMethod: () => null,
  setComment: () => null,
  setEmailOnSuccess: () => null,
  setFilename: () => null,
  setFormatType: () => null,
  setGhostCountsCalculated: () => null,
  setInvalidNpis: () => null,
  setInvalidTins: () => null,
  setIsDistinctServicingProviderReport: () => null,
  setPage: () => null,
  setProjects: () => null,
  setResultsSummary: () => null,
  setReviewProjects: () => null,
  setSelectedLineOfBusiness: () => null,
  setSelectedProjectIds: () => null,
  setTerminateByAllLocations: () => null,
  setTerminateByAllSpecialties: () => null,
  setTerminateByNpi: () => null,
  setTerminateByTin: () => null,
  setTerminationReason: () => null,
  updateIncludedReports: () => null,
});

const TerminationsWizardProvider = (props: VolumeTerminationsProps & { children: React.ReactNode }): JSX.Element => {
  const projectService = new ProjectService();
  const providerTerminationService = new ProviderTerminationService();
  const pciDashboardService = new PciDashboardService();
  const featureFlagContext = useContext(FeatureFlagContext).enabledFeatureFlags;

  const [bulkImportFormData, setBulkImportFormData] = useState<FormData>(null);
  const [bulkProviderTerminationId, setBulkProviderTerminationId] = useState<number>(null);
  const [comment, setComment] = useState("");
  const [emailOnSuccess, setEmailOnSuccess] = useState(true);
  const [filename, setFilename] = useState(reportName(props.terminationType == "ghost" ? "GhostVolumeTerminations" : "VolumeTerminations"));
  const [formatType, setFormatType] = useState("CSV");
  const [ghostCountsCalculated, setGhostCountsCalculated] = useState(false);
  const [includedReports, setIncludedReports] = useState<IncludedReports>({
    projectDetailsTerminationOverview: true,
    serviceAreaSpecialtySummaryChangedGaps: true,
    serviceAreaSpecialtySummaryComparison: true,
    opportunitySpecialtySummaryChangedGaps: true,
    opportunitySpecialtySummaryComparison: true,
    terminationUploadFile: !(props.terminationType == "ghost" && featureFlagContext.has(Features.VOLUME_GHOST_PROVIDER_TERMINATIONS)) ? true : false,
    serviceAreaProviders: false,
    ghostTerminationList: props.terminationType == "ghost" && featureFlagContext.has(Features.VOLUME_GHOST_PROVIDER_TERMINATIONS) ? true : false,
    opportunityProviders: false,
  });
  const [invalidNpis, setInvalidNpis] = useState<InvalidNpiTableData[]>([]);
  const [invalidTins, setInvalidTins] = useState<InvalidTinTableData[]>([]);
  const [isDistinctServicingProviderReport, setIsDistinctServicingProviderReport] = useState<boolean>(false);
  const [bulkProviderTerminationMethod, setBulkProviderTerminationMethod] = useState<BulkProviderTerminationMethod>(props.terminationType == "ghost" && featureFlagContext.has(Features.VOLUME_GHOST_PROVIDER_TERMINATIONS) ? BulkProviderTerminationMethod.Ghost : BulkProviderTerminationMethod.Import);
  const [page, setPage] = useState<TerminationsWizardContextSteps>(TerminationsWizardContextSteps.SelectProjects);
  const [projects, setProjects] = useState<BulkProviderChangeProjectTableData[]>([]);
  const [resultsSummary, setResultsSummary] = useState<ResultsSummaryTableData[]>([]);
  const [reviewProjects, setReviewProjects] = useState<ReviewProjectsTableData[]>([]);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState("Total");
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [terminateByAllLocations, setTerminateByAllLocations] = useState(true);
  const [terminateByAllSpecialties, setTerminateByAllSpecialties] = useState(true);
  const [terminateByNpi, setTerminateByNpi] = useState(true);
  const [terminateByTin, setTerminateByTin] = useState(false);
  const [terminationReason, setTerminationReason] = useState("");

  const updateIncludedReports = (changedReport: keyof IncludedReports, newValue: boolean) => {
    setIncludedReports((currentIncludedReports) => ({
      ...currentIncludedReports,
      [changedReport]: newValue,
    }))
  }

  const value = {
    projectService,
    providerTerminationService,
    pciDashboardService,

    bulkImportFormData,
    bulkProviderTerminationId,
    bulkProviderTerminationMethod,
    comment,
    emailOnSuccess,
    filename,
    formatType,
    ghostCountsCalculated,
    includedReports,
    invalidNpis,
    invalidTins,
    isDistinctServicingProviderReport,
    page,
    projects,
    resultsSummary,
    reviewProjects,
    selectedLineOfBusiness,
    selectedProjectIds,
    terminateByAllLocations,
    terminateByAllSpecialties,
    terminateByNpi,
    terminateByTin,
    terminationReason,

    setBulkImportFormData,
    setBulkProviderTerminationId,
    setBulkProviderTerminationMethod,
    setComment,
    setEmailOnSuccess,
    setFilename,
    setFormatType,
    setGhostCountsCalculated,
    setInvalidNpis,
    setInvalidTins,
    setIsDistinctServicingProviderReport,
    setPage,
    setProjects,
    setResultsSummary,
    setReviewProjects,
    setSelectedLineOfBusiness,
    setSelectedProjectIds,
    setTerminateByAllLocations,
    setTerminateByAllSpecialties,
    setTerminateByNpi,
    setTerminateByTin,
    setTerminationReason,
    updateIncludedReports,
  };
  /* eslint-enable sort-keys */

  return (
    <TerminationsWizardContext.Provider value={value}>
      {props.children}
    </TerminationsWizardContext.Provider>
  );

};

export { TerminationsWizardContext, TerminationsWizardProvider };
